export type MovieType = "movie" | "series" | "variety";

export interface Movie {
  id: string;
  title: string;
  type: MovieType;
  poster: string;
  year: number;
  region: string;
  genres: string[];
  rating: number;
  director: string;
  cast: string;
  duration: string;
  synopsis: string;
  views: number;
  updatedAt: string;
}

export interface Banner {
  id: string;
  movieId: string;
  title: string;
  subtitle: string;
  image: string;
  tag: string;
}

export const typeLabels: Record<MovieType, string> = {
  movie: "电影",
  series: "电视剧",
  variety: "综艺",
};

export const regions: string[] = ["全部", "中国大陆", "中国香港", "美国", "日本", "韩国", "英国", "法国"];

export const years: string[] = [
  "全部",
  "2026",
  "2025",
  "2024",
  "2023",
  "2022",
  "2021",
  "2020",
  "更早",
];

export const genreOptions: Record<MovieType, string[]> = {
  movie: ["全部", "科幻", "动作", "悬疑", "爱情", "剧情", "奇幻", "冒险"],
  series: ["全部", "都市", "古装", "悬疑", "医疗", "武侠", "科幻", "青春"],
  variety: ["全部", "真人秀", "音乐", "美食", "竞技", "亲子", "访谈", "生活"],
};

export const sortOptions: { key: string; label: string }[] = [
  { key: "latest", label: "最近更新" },
  { key: "popular", label: "最多播放" },
  { key: "rating", label: "评分最高" },
  { key: "year", label: "年份最新" },
];

export const banners: Banner[] = [
  {
    id: "1",
    movieId: "1",
    title: "星际远征",
    subtitle: "人类舰队穿越虫洞，寻找下一个家园的史诗之旅",
    image:
      "https://readdy.ai/api/search-image?query=Cinematic%20sci-fi%20film%20banner%2C%20vast%20fleet%20of%20spaceships%20silhouetted%20against%20a%20giant%20glowing%20wormhole%2C%20deep%20space%20nebula%20of%20teal%20and%20amber%2C%20dramatic%20rim%20lighting%2C%20epic%20atmospheric%20scale%2C%20high%20detail%20key%20art%2C%20dark%20moody%20background%2C%20no%20text&width=1920&height=1080&seq=banner-01&orientation=landscape",
    tag: "科幻钜制",
  },
  {
    id: "2",
    movieId: "2",
    title: "长安夜行",
    subtitle: "一夜之间，长安城暗流涌动，少年捕快揭开惊天阴谋",
    image:
      "https://readdy.ai/api/search-image?query=Cinematic%20ancient%20Chinese%20dynasty%20film%20banner%2C%20moonlit%20traditional%20city%20street%20with%20red%20lanterns%2C%20lone%20figure%20in%20dark%20robe%20walking%20under%20rain%2C%20warm%20amber%20glow%20against%20deep%20night%20tones%2C%20dramatic%20atmospheric%20lighting%2C%20high%20detail%20key%20art%2C%20no%20text&width=1920&height=1080&seq=banner-02&orientation=landscape",
    tag: "古装悬疑",
  },
  {
    id: "3",
    movieId: "3",
    title: "深海追光",
    subtitle: "潜入万米深渊，寻找失落的文明遗迹",
    image:
      "https://readdy.ai/api/search-image?query=Cinematic%20underwater%20adventure%20film%20banner%2C%20deep%20ocean%20abyss%20with%20glowing%20bioluminescent%20creatures%20and%20a%20small%20submarine%20spotlight%2C%20teal%20and%20emerald%20water%20tones%2C%20rays%20of%20light%20from%20above%2C%20epic%20scale%2C%20high%20detail%20key%20art%2C%20no%20text&width=1920&height=1080&seq=banner-03&orientation=landscape",
    tag: "冒险巨作",
  },
  {
    id: "4",
    movieId: "8",
    title: "龙腾九州",
    subtitle: "少年觉醒远古血脉，守护九州苍生的奇幻史诗",
    image:
      "https://readdy.ai/api/search-image?query=Cinematic%20fantasy%20film%20banner%2C%20colossal%20oriental%20dragon%20coiling%20through%20storm%20clouds%20above%20ancient%20mountains%2C%20green%20and%20gold%20lighting%2C%20epic%20mythological%20scale%2C%20dramatic%20backlight%2C%20high%20detail%20key%20art%2C%20dark%20atmospheric%20background%2C%20no%20text&width=1920&height=1080&seq=banner-04&orientation=landscape",
    tag: "奇幻热播",
  },
  {
    id: "5",
    movieId: "11",
    title: "都市恋歌",
    subtitle: "在钢铁森林里，他们擦肩而过又彼此靠近",
    image:
      "https://readdy.ai/api/search-image?query=Cinematic%20modern%20romance%20drama%20banner%2C%20rainy%20city%20street%20at%20night%20with%20bokeh%20lights%2C%20two%20figures%20with%20umbrellas%20almost%20meeting%2C%20warm%20amber%20and%20soft%20green%20tones%2C%20dreamy%20atmospheric%20lighting%2C%20high%20detail%20key%20art%2C%20no%20text&width=1920&height=1080&seq=banner-05&orientation=landscape",
    tag: "都市情感",
  },
];

export const movies: Movie[] = [
  {
    id: "1",
    title: "星际远征",
    type: "movie",
    poster:
      "https://readdy.ai/api/search-image?query=Cinematic%20sci-fi%20movie%20poster%2C%20lone%20astronaut%20silhouetted%20before%20a%20giant%20glowing%20wormhole%20in%20deep%20space%2C%20teal%20and%20amber%20nebula%2C%20dramatic%20rim%20lighting%2C%20epic%20scale%2C%20simple%20dark%20background%2C%20high%20detail%20key%20art%2C%20no%20text&width=600&height=900&seq=poster-movie-01&orientation=portrait",
    year: 2026,
    region: "美国",
    genres: ["科幻", "冒险"],
    rating: 9.1,
    director: "林·卡佛",
    cast: "陈默 / 艾莉·沃森 / 高桥凉介",
    duration: "142分钟",
    synopsis:
      "当地球资源枯竭，人类将最后希望寄托于虫洞另一端的未知星系。一支由精锐宇航员组成的远征舰队踏上穿越之旅，却在途中遭遇时空错乱与文明遗迹的双重考验。面对未知的恐惧与同伴的牺牲，他们必须做出改变人类命运的抉择。",
    views: 12840000,
    updatedAt: "2026-10-08",
  },
  {
    id: "2",
    title: "长安夜行",
    type: "movie",
    poster:
      "https://readdy.ai/api/search-image?query=Cinematic%20ancient%20Chinese%20dynasty%20movie%20poster%2C%20lone%20figure%20in%20dark%20robe%20holding%20a%20lantern%20on%20a%20moonlit%20cobblestone%20street%20with%20red%20lanterns%2C%20warm%20amber%20glow%20against%20deep%20night%20tones%2C%20simple%20dark%20background%2C%20high%20detail%20key%20art%2C%20no%20text&width=600&height=900&seq=poster-movie-02&orientation=portrait",
    year: 2025,
    region: "中国大陆",
    genres: ["悬疑", "剧情"],
    rating: 8.8,
    director: "顾长明",
    cast: "李砚 / 周知遥 / 王守一",
    duration: "128分钟",
    synopsis:
      "大唐盛世的长安城，一夜之间暗流涌动。年轻捕快奉命调查一桩离奇命案，却意外牵出朝堂与江湖交织的巨大阴谋。在真相与谎言的迷雾中，他必须凭借智慧与勇气，于天明之前找出幕后黑手。",
    views: 9860000,
    updatedAt: "2026-10-06",
  },
  {
    id: "3",
    title: "深海追光",
    type: "movie",
    poster:
      "https://readdy.ai/api/search-image?query=Cinematic%20underwater%20adventure%20movie%20poster%2C%20small%20submarine%20with%20spotlight%20descending%20into%20a%20dark%20ocean%20abyss%20surrounded%20by%20glowing%20bioluminescent%20creatures%2C%20teal%20and%20emerald%20tones%2C%20simple%20dark%20background%2C%20high%20detail%20key%20art%2C%20no%20text&width=600&height=900&seq=poster-movie-03&orientation=portrait",
    year: 2025,
    region: "中国大陆",
    genres: ["冒险", "科幻"],
    rating: 8.4,
    director: "方远",
    cast: "沈川 / 叶澜 / 吴一鸣",
    duration: "135分钟",
    synopsis:
      "一支国际科考队驾驶微型潜艇潜入万米深渊，寻找传说中沉没的远古文明遗迹。随着下潜深度不断增加，他们发现深海中隐藏着远超想象的秘密，也面临着来自黑暗深处最原始的威胁。",
    views: 7640000,
    updatedAt: "2026-10-04",
  },
  {
    id: "4",
    title: "荒野猎手",
    type: "movie",
    poster:
      "https://readdy.ai/api/search-image?query=Cinematic%20action%20thriller%20movie%20poster%2C%20lone%20hunter%20with%20rifle%20standing%20in%20a%20misty%20wilderness%20forest%20at%20dawn%2C%20cold%20green%20and%20grey%20tones%2C%20dramatic%20backlight%2C%20simple%20dark%20background%2C%20high%20detail%20key%20art%2C%20no%20text&width=600&height=900&seq=poster-movie-04&orientation=portrait",
    year: 2024,
    region: "美国",
    genres: ["动作", "冒险"],
    rating: 7.9,
    director: "杰克·莫罗",
    cast: "卢卡斯·里德 / 安娜·贝尔 / 托马斯·凯恩",
    duration: "118分钟",
    synopsis:
      "退役特种兵隐居荒野，却因一场突如其来的追猎被迫重回丛林战场。在没有任何支援的情况下，他必须利用对自然的全部了解，与训练有素的猎杀者展开一场生死博弈。",
    views: 6890000,
    updatedAt: "2026-09-28",
  },
  {
    id: "5",
    title: "时空信笺",
    type: "movie",
    poster:
      "https://readdy.ai/api/search-image?query=Cinematic%20romantic%20sci-fi%20movie%20poster%2C%20a%20glowing%20old%20letter%20floating%20above%20a%20clock%20face%20dissolving%20into%20starlight%2C%20soft%20teal%20and%20warm%20amber%20glow%2C%20dreamy%20atmosphere%2C%20simple%20dark%20background%2C%20high%20detail%20key%20art%2C%20no%20text&width=600&height=900&seq=poster-movie-05&orientation=portrait",
    year: 2026,
    region: "日本",
    genres: ["爱情", "科幻"],
    rating: 8.6,
    director: "中村翔太",
    cast: "木村遥 / 佐藤樱 / 铃木凛",
    duration: "124分钟",
    synopsis:
      "少女无意间收到一封来自十年后的信，落款竟是未来的自己。随着一封封信件穿越时空抵达，她逐渐发现，这些文字不仅关乎一段未能说出口的感情，更指向一个必须被改变的结局。",
    views: 8320000,
    updatedAt: "2026-10-02",
  },
  {
    id: "6",
    title: "迷雾之城",
    type: "movie",
    poster:
      "https://readdy.ai/api/search-image?query=Cinematic%20noir%20crime%20movie%20poster%2C%20detective%20in%20a%20long%20coat%20standing%20in%20a%20foggy%20neon-lit%20city%20alley%20at%20night%2C%20green%20and%20amber%20reflections%20on%20wet%20pavement%2C%20simple%20dark%20background%2C%20high%20detail%20key%20art%2C%20no%20text&width=600&height=900&seq=poster-movie-06&orientation=portrait",
    year: 2025,
    region: "中国香港",
    genres: ["悬疑", "剧情"],
    rating: 8.2,
    director: "陈嘉豪",
    cast: "黄志诚 / 林慧敏 / 郑国雄",
    duration: "121分钟",
    synopsis:
      "一桩尘封多年的悬案重新浮出水面，老刑警与年轻搭档在迷雾笼罩的城市中展开调查。真相被层层谎言包裹，每个人都藏着不可告人的秘密，而追查者自己也被卷入了深渊。",
    views: 5980000,
    updatedAt: "2026-09-25",
  },
  {
    id: "7",
    title: "山河故人归",
    type: "movie",
    poster:
      "https://readdy.ai/api/search-image?query=Cinematic%20drama%20movie%20poster%2C%20a%20solitary%20figure%20standing%20on%20a%20vast%20misty%20mountainside%20overlooking%20a%20valley%20at%20golden%20hour%2C%20warm%20and%20muted%20green%20tones%2C%20simple%20dark%20background%2C%20high%20detail%20key%20art%2C%20no%20text&width=600&height=900&seq=poster-movie-07&orientation=portrait",
    year: 2024,
    region: "中国大陆",
    genres: ["剧情"],
    rating: 8.9,
    director: "贾平川",
    cast: "赵大山 / 刘玉兰 / 孙向阳",
    duration: "139分钟",
    synopsis:
      "离家多年的游子回到故乡，发现熟悉的山河早已改变模样。在重建家园的过程中，他与亲人、乡亲之间尘封的记忆被一一唤醒，也重新理解了离别与归来的意义。",
    views: 5210000,
    updatedAt: "2026-09-20",
  },
  {
    id: "8",
    title: "龙腾九州",
    type: "movie",
    poster:
      "https://readdy.ai/api/search-image?query=Cinematic%20fantasy%20movie%20poster%2C%20colossal%20oriental%20dragon%20coiling%20through%20storm%20clouds%20above%20ancient%20mountains%2C%20green%20and%20gold%20lighting%2C%20epic%20mythological%20scale%2C%20simple%20dark%20background%2C%20high%20detail%20key%20art%2C%20no%20text&width=600&height=900&seq=poster-movie-08&orientation=portrait",
    year: 2026,
    region: "中国大陆",
    genres: ["奇幻", "动作"],
    rating: 8.5,
    director: "陆天行",
    cast: "江辰 / 苏晚晴 / 白墨",
    duration: "146分钟",
    synopsis:
      "平凡少年体内沉睡的远古龙族血脉意外觉醒，从此被卷入守护九州苍生的宏大命运。他必须在各方势力的角力中成长，直面自己真正想要守护的东西。",
    views: 11480000,
    updatedAt: "2026-10-09",
  },
  {
    id: "9",
    title: "无声告白",
    type: "movie",
    poster:
      "https://readdy.ai/api/search-image?query=Cinematic%20intimate%20drama%20movie%20poster%2C%20a%20young%20woman%20looking%20out%20of%20a%20rain-streaked%20window%20at%20soft%20morning%20light%2C%20muted%20green%20and%20warm%20neutral%20tones%2C%20quiet%20melancholic%20mood%2C%20simple%20dark%20background%2C%20high%20detail%20key%20art%2C%20no%20text&width=600&height=900&seq=poster-movie-09&orientation=portrait",
    year: 2025,
    region: "法国",
    genres: ["剧情", "爱情"],
    rating: 8.3,
    director: "克莱尔·杜邦",
    cast: "玛尔戈·勒费弗 / 皮埃尔·罗兰 / 苏菲·马丁",
    duration: "112分钟",
    synopsis:
      "在一座安静的小城里，两个彼此靠近却始终无法开口的灵魂，用最细微的方式表达着爱意。一段没有说出口的告白，在岁月里慢慢发酵，成为彼此一生最深的牵挂。",
    views: 4360000,
    updatedAt: "2026-09-18",
  },
  {
    id: "10",
    title: "极速幻影",
    type: "movie",
    poster:
      "https://readdy.ai/api/search-image?query=Cinematic%20action%20racing%20movie%20poster%2C%20a%20sleek%20race%20car%20drifting%20through%20a%20night%20city%20street%20with%20motion%20blur%20and%20sparks%2C%20teal%20and%20orange%20light%20streaks%2C%20simple%20dark%20background%2C%20high%20detail%20key%20art%2C%20no%20text&width=600&height=900&seq=poster-movie-10&orientation=portrait",
    year: 2024,
    region: "韩国",
    genres: ["动作", "冒险"],
    rating: 7.7,
    director: "金泰勋",
    cast: "朴志勋 / 崔允儿 / 李宰赫",
    duration: "126分钟",
    synopsis:
      "曾经的天才车手因一场事故退出赛道，多年后为守护家人被迫重返地下赛车世界。面对昔日的对手与全新的规则，他必须在速度与理智之间找到唯一的出路。",
    views: 6120000,
    updatedAt: "2026-09-15",
  },
  {
    id: "11",
    title: "都市恋歌",
    type: "series",
    poster:
      "https://readdy.ai/api/search-image?query=Cinematic%20modern%20urban%20romance%20series%20poster%2C%20two%20figures%20with%20umbrellas%20almost%20meeting%20on%20a%20rainy%20city%20street%20at%20night%20with%20bokeh%20lights%2C%20warm%20amber%20and%20soft%20green%20tones%2C%20simple%20dark%20background%2C%20high%20detail%20key%20art%2C%20no%20text&width=600&height=900&seq=poster-series-11&orientation=portrait",
    year: 2026,
    region: "中国大陆",
    genres: ["都市", "爱情"],
    rating: 8.7,
    director: "林小柔",
    cast: "顾清 / 陆一凡 / 江语桐",
    duration: "45分钟/集",
    synopsis:
      "两个在都市中为梦想打拼的年轻人，因为一场雨中偶遇而相识。他们在快节奏的生活里彼此扶持，也在现实的考验中不断擦肩。当理想与感情发生碰撞，他们能否守住最初的约定？",
    views: 15620000,
    updatedAt: "2026-10-10",
  },
  {
    id: "12",
    title: "权谋天下",
    type: "series",
    poster:
      "https://readdy.ai/api/search-image?query=Cinematic%20ancient%20palace%20intrigue%20series%20poster%2C%20a%20royal%20figure%20in%20dark%20embroidered%20robes%20seated%20before%20a%20grand%20throne%20hall%2C%20green%20and%20gold%20candlelight%2C%20dramatic%20chiaroscuro%2C%20simple%20dark%20background%2C%20high%20detail%20key%20art%2C%20no%20text&width=600&height=900&seq=poster-series-12&orientation=portrait",
    year: 2025,
    region: "中国大陆",
    genres: ["古装", "悬疑"],
    rating: 9.0,
    director: "郑道源",
    cast: "王侯 / 沈砚 / 柳如烟",
    duration: "48分钟/集",
    synopsis:
      "朝堂之上，暗潮汹涌。一位出身寒门的年轻官员凭借过人的谋略逐步接近权力中心，却发现每一步博弈都牵动着家国命运。在这场没有硝烟的战争中，谁才是真正的执棋者？",
    views: 18340000,
    updatedAt: "2026-10-07",
  },
  {
    id: "13",
    title: "医者仁心",
    type: "series",
    poster:
      "https://readdy.ai/api/search-image?query=Cinematic%20medical%20drama%20series%20poster%2C%20a%20doctor%20in%20a%20white%20coat%20standing%20in%20a%20bright%20hospital%20corridor%20with%20soft%20green%20and%20cool%20tones%2C%20calm%20determined%20expression%2C%20simple%20dark%20background%2C%20high%20detail%20key%20art%2C%20no%20text&width=600&height=900&seq=poster-series-13&orientation=portrait",
    year: 2025,
    region: "中国大陆",
    genres: ["医疗", "剧情"],
    rating: 8.6,
    director: "许安然",
    cast: "张启明 / 何静 / 徐子轩",
    duration: "42分钟/集",
    synopsis:
      "一群奋战在急诊一线的医护人员，每天与时间赛跑，与死神博弈。在挽救生命的同时，他们也在面对各自的困惑与抉择，用专业与温度诠释着医者的责任与担当。",
    views: 11270000,
    updatedAt: "2026-10-01",
  },
  {
    id: "14",
    title: "江湖夜雨",
    type: "series",
    poster:
      "https://readdy.ai/api/search-image?query=Cinematic%20wuxia%20martial%20arts%20series%20poster%2C%20a%20swordsman%20in%20flowing%20robes%20standing%20on%20a%20bamboo%20rooftop%20under%20falling%20rain%20at%20night%2C%20green%20and%20ink%20black%20tones%2C%20atmospheric%20mood%2C%20simple%20dark%20background%2C%20high%20detail%20key%20art%2C%20no%20text&width=600&height=900&seq=poster-series-14&orientation=portrait",
    year: 2024,
    region: "中国大陆",
    genres: ["武侠", "古装"],
    rating: 8.5,
    director: "叶孤鸿",
    cast: "萧远 / 苏清歌 / 云中鹤",
    duration: "46分钟/集",
    synopsis:
      "江湖恩怨，儿女情长。一柄失落多年的神兵重现江湖，牵出各大门派争夺的秘密。孤身行走的侠客为查明师门旧案，踏上一条充满刀光剑影的未知之路。",
    views: 9940000,
    updatedAt: "2026-09-26",
  },
  {
    id: "15",
    title: "未来档案",
    type: "series",
    poster:
      "https://readdy.ai/api/search-image?query=Cinematic%20sci-fi%20series%20poster%2C%20a%20figure%20silhouetted%20in%20a%20futuristic%20dark%20control%20room%20surrounded%20by%20glowing%20green%20holographic%20data%20screens%2C%20high%20tech%20atmosphere%2C%20simple%20dark%20background%2C%20high%20detail%20key%20art%2C%20no%20text&width=600&height=900&seq=poster-series-15&orientation=portrait",
    year: 2026,
    region: "美国",
    genres: ["科幻", "悬疑"],
    rating: 8.8,
    director: "艾米·卡特",
    cast: "诺亚·布莱克 / 艾拉·格林 / 詹姆斯·福克斯",
    duration: "50分钟/集",
    synopsis:
      "在不远的未来，一支特殊调查小组负责处理那些无法用常理解释的档案。随着一个个悬案被揭开，他们逐渐意识到，这些看似孤立的事件背后，指向一个足以颠覆认知的巨大真相。",
    views: 12680000,
    updatedAt: "2026-10-05",
  },
  {
    id: "16",
    title: "少年游侠记",
    type: "series",
    poster:
      "https://readdy.ai/api/search-image?query=Cinematic%20youth%20adventure%20series%20poster%2C%20a%20young%20wanderer%20with%20a%20travel%20pack%20standing%20at%20the%20edge%20of%20a%20green%20valley%20at%20sunrise%2C%20bright%20hopeful%20green%20and%20gold%20tones%2C%20simple%20dark%20background%2C%20high%20detail%20key%20art%2C%20no%20text&width=600&height=900&seq=poster-series-16&orientation=portrait",
    year: 2025,
    region: "中国大陆",
    genres: ["青春", "古装"],
    rating: 8.1,
    director: "唐小舟",
    cast: "何以风 / 苏糖 / 阿宝",
    duration: "40分钟/集",
    synopsis:
      "一个怀揣侠客梦的山村少年背起行囊走出家门，一路上结识挚友、历经奇遇。天真的理想在现实中被不断打磨，但他始终没有忘记出发时那颗炽热的心。",
    views: 7230000,
    updatedAt: "2026-09-22",
  },
  {
    id: "17",
    title: "快乐盛典",
    type: "variety",
    poster:
      "https://readdy.ai/api/search-image?query=Colorful%20variety%20tv%20show%20poster%2C%20a%20festive%20stage%20with%20bright%20spotlights%20and%20confetti%20surrounded%20by%20joyful%20silhouettes%2C%20vibrant%20green%20and%20orange%20lights%2C%20energetic%20party%20atmosphere%2C%20simple%20dark%20background%2C%20high%20detail%20design%2C%20no%20text&width=600&height=900&seq=poster-variety-17&orientation=portrait",
    year: 2026,
    region: "中国大陆",
    genres: ["真人秀", "访谈"],
    rating: 7.8,
    director: "综艺制作组",
    cast: "主持群 / 明星嘉宾",
    duration: "90分钟/期",
    synopsis:
      "一档轻松欢乐的舞台综艺，每期邀请不同的明星嘉宾同台互动，用游戏、访谈与才艺表演为观众带来满格快乐。笑声不断，惊喜连连，是全家共享的下饭综艺。",
    views: 14260000,
    updatedAt: "2026-10-11",
  },
  {
    id: "18",
    title: "星光大道",
    type: "variety",
    poster:
      "https://readdy.ai/api/search-image?query=Talent%20competition%20tv%20show%20poster%2C%20a%20single%20performer%20under%20a%20dramatic%20green%20spotlight%20on%20a%20grand%20dark%20stage%2C%20glowing%20audience%20silhouettes%2C%20dramatic%20spotlight%20beams%2C%20simple%20dark%20background%2C%20high%20detail%20design%2C%20no%20text&width=600&height=900&seq=poster-variety-18&orientation=portrait",
    year: 2026,
    region: "中国大陆",
    genres: ["音乐", "竞技"],
    rating: 8.0,
    director: "综艺制作组",
    cast: "导师团 / 选手阵容",
    duration: "100分钟/期",
    synopsis:
      "面向普通人的才艺竞技舞台，来自各行各业的选手怀揣梦想登台，用歌声与表演打动评委与观众。每一次登台都是通往更广阔舞台的起点。",
    views: 11930000,
    updatedAt: "2026-10-09",
  },
  {
    id: "19",
    title: "美食环游记",
    type: "variety",
    poster:
      "https://readdy.ai/api/search-image?query=Food%20travel%20variety%20show%20poster%2C%20a%20rustic%20wooden%20table%20filled%20with%20colorful%20fresh%20dishes%20and%20steaming%20bowls%2C%20warm%20green%20and%20amber%20natural%20light%2C%20cozy%20appetizing%20atmosphere%2C%20simple%20dark%20background%2C%20high%20detail%20design%2C%20no%20text&width=600&height=900&seq=poster-variety-19&orientation=portrait",
    year: 2025,
    region: "中国大陆",
    genres: ["美食", "生活"],
    rating: 8.4,
    director: "综艺制作组",
    cast: "美食探员 / 明星嘉宾",
    duration: "60分钟/期",
    synopsis:
      "跟随镜头走遍大江南北，寻找藏在街头巷尾的地道风味。每一道菜的背后，都是一方水土的故事与人情。用美食连接城市与记忆，感受最真实的人间烟火。",
    views: 8750000,
    updatedAt: "2026-10-03",
  },
  {
    id: "20",
    title: "极限飞跃",
    type: "variety",
    poster:
      "https://readdy.ai/api/search-image?query=Extreme%20outdoor%20challenge%20variety%20show%20poster%2C%20contestants%20leaping%20between%20high%20platforms%20over%20dramatic%20mountain%20scenery%2C%20dynamic%20action%20motion%2C%20green%20and%20orange%20accent%20lighting%2C%20simple%20dark%20background%2C%20high%20detail%20design%2C%20no%20text&width=600&height=900&seq=poster-variety-20&orientation=portrait",
    year: 2026,
    region: "中国大陆",
    genres: ["竞技", "真人秀"],
    rating: 7.9,
    director: "综艺制作组",
    cast: "固定嘉宾团",
    duration: "95分钟/期",
    synopsis:
      "明星嘉宾走出演播厅，深入壮美的自然实景完成重重挑战。在极限任务中展现勇气与默契，也让观众感受到挑战自我、突破边界的无限可能。",
    views: 9130000,
    updatedAt: "2026-10-07",
  },
  {
    id: "21",
    title: "歌手之路",
    type: "variety",
    poster:
      "https://readdy.ai/api/search-image?query=Music%20competition%20tv%20show%20poster%2C%20a%20singer%20with%20a%20microphone%20silhouetted%20against%20warm%20stage%20lights%20and%20sound%20wave%20patterns%2C%20green%20and%20gold%20vibrant%20tones%2C%20simple%20dark%20background%2C%20high%20detail%20design%2C%20no%20text&width=600&height=900&seq=poster-variety-21&orientation=portrait",
    year: 2025,
    region: "中国大陆",
    genres: ["音乐", "竞技"],
    rating: 8.2,
    director: "综艺制作组",
    cast: "实力唱将 / 新生代歌手",
    duration: "110分钟/期",
    synopsis:
      "一场关于声音的较量，实力歌手同台竞演，呈现风格各异的舞台演绎。他们用音乐讲述故事，也在竞争中彼此成就，为观众带来一场场视听盛宴。",
    views: 10820000,
    updatedAt: "2026-10-06",
  },
  {
    id: "22",
    title: "萌宠日记",
    type: "variety",
    poster:
      "https://readdy.ai/api/search-image?query=Heartwarming%20pet%20variety%20show%20poster%2C%20cute%20cats%20and%20dogs%20playing%20together%20in%20a%20cozy%20bright%20living%20room%20with%20soft%20green%20plants%2C%20warm%20gentle%20lighting%2C%20adorable%20playful%20mood%2C%20simple%20dark%20background%2C%20high%20detail%20design%2C%20no%20text&width=600&height=900&seq=poster-variety-22&orientation=portrait",
    year: 2025,
    region: "日本",
    genres: ["亲子", "生活"],
    rating: 8.6,
    director: "综艺制作组",
    cast: "萌宠家族",
    duration: "45分钟/期",
    synopsis:
      "记录一群可爱毛孩子的日常，从早晨的第一缕阳光到夜晚的安稳睡眠。它们用最简单的方式治愈人心，也让人重新发现生活中那些被忽略的温暖瞬间。",
    views: 7460000,
    updatedAt: "2026-09-30",
  },
  {
    id: "23",
    title: "暗夜追踪",
    type: "movie",
    poster:
      "https://readdy.ai/api/search-image?query=Cinematic%20crime%20thriller%20movie%20poster%2C%20a%20shadowy%20figure%20walking%20down%20a%20rain-soaked%20empty%20street%20at%20midnight%20under%20flickering%20green%20streetlights%2C%20tense%20suspenseful%20mood%2C%20simple%20dark%20background%2C%20high%20detail%20key%20art%2C%20no%20text&width=600&height=900&seq=poster-movie-23&orientation=portrait",
    year: 2026,
    region: "中国香港",
    genres: ["悬疑", "动作"],
    rating: 8.3,
    director: "梁文轩",
    cast: "程锋 / 苏雅 / 高天",
    duration: "119分钟",
    synopsis:
      "一名卧底警探潜伏多年，终于触碰到犯罪集团的核心秘密。然而真相越近，危险也越近。在身份即将暴露的边缘，他必须完成最后一次孤注一掷的追踪。",
    views: 6670000,
    updatedAt: "2026-10-08",
  },
  {
    id: "24",
    title: "山海奇缘",
    type: "series",
    poster:
      "https://readdy.ai/api/search-image?query=Cinematic%20costume%20fantasy%20series%20poster%2C%20a%20young%20woman%20in%20flowing%20green%20and%20gold%20ancient%20robes%20standing%20before%20mythical%20mountains%20and%20a%20glowing%20ancient%20beast%2C%20ethereal%20mist%20and%20warm%20light%2C%20simple%20dark%20background%2C%20high%20detail%20key%20art%2C%20no%20text&width=600&height=900&seq=poster-series-24&orientation=portrait",
    year: 2026,
    region: "中国大陆",
    genres: ["古装", "奇幻"],
    rating: 8.9,
    director: "白景行",
    cast: "云舒 / 墨言 / 青丘",
    duration: "44分钟/集",
    synopsis:
      "山海之间，人妖共存。一位身负神秘血脉的少女，为解开自身命运的谜题踏上旅途。她与守护者相遇相知，在纷争与守护中，逐步揭开上古传说的真相。",
    views: 13490000,
    updatedAt: "2026-10-10",
  },
];